/**
 * Deterministic route interpreter for the bot harness.
 * Usable in vitest (Node) and in the browser page.
 *
 * A route is a sequence of phases; each phase drives player input until its
 * `until` condition is met.  Phases advance sequentially; the bot stops when
 * the timer reaches 'finished' or maxTicks is exhausted.
 */

import { TICK_DT } from '../movement/constants';
import type { PlayerController } from '../movement/PlayerController';
import type { RunTimer } from './RunTimer';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Condition that ends a phase.  All specified fields are AND-ed together. */
export interface Until {
  /** Ticks elapsed *within this phase* (not total run ticks). */
  tick?: number;
  /** timer.splits.length >= n  (checkpoint count). */
  checkpoint?: number;
  /** player.position.z >= value  (z-axis, +forward = -Z so this is "past" in standard use). */
  zPast?: number;
  /** player.position.z <= value. */
  zBefore?: number;
  /** player.position.x >= value. */
  xPast?: number;
  /** player.position.x <= value. */
  xBefore?: number;
  /**
   * Phase ends when horizontal distance from player to point is < radius.
   * Suitable for curved lanes where world-axis conditions are awkward.
   */
  nearXZ?: { x: number; z: number; radius: number };
}

/**
 * featherX: strafe gently toward the ramp face.
 *   - side === -1: hold right=-1 while player.position.x > threshold  (into right ramp)
 *   - side ===  1: hold right=+1 while player.position.x < threshold  (into left ramp)
 *   forward=0, jump=false.
 */
/**
 * carve: turns view toward yawDeg (at degPerTick per tick) while holding a
 * constant strafe input.  Mirrors real player technique on curved lanes: you
 * press into the ramp and turn your view to follow the lane heading, which
 * generates speed via clipVelocity geometry.
 *
 * `right` is the strafe direction: +1 = right-face (into a right-turning lane),
 * -1 = left-face.  forward is always 0.
 */
/**
 * featherLane: feather toward the ramp face using the LANE's local coordinate
 * frame rather than world-X.  Designed for curved stages where the lane heading
 * rotates and world-X drifts out of alignment with the ramp face.
 *
 * Semantics:
 *   - The lane frame right vector at headingDeg is rightVec = (cos h, 0, −sin h).
 *   - Signed lateral distance from lane centre: d = dot(pos − origin, rightVec).
 *   - strafe input.right = side when d*(side) < offset*(side); else 0.
 *     (mirrors featherX: press toward the face when drifted past the target offset)
 *   - If yawFollow=true, the internal heading advances from headingDeg toward
 *     targetHeadingDeg at turnPerTick deg/tick; the yaw passed to player.tick
 *     tracks this (headingRad + PI).
 *   - offset is typically width/4; positive = right side of lane (right face).
 *   - side=-1 means the ramp face is on the left of the lane; side=+1 right.
 */
export type RoutePhase =
  | { kind: 'featherX'; threshold: number; side: 1 | -1; until: Until }
  | { kind: 'hold'; forward: number; right: number; until: Until }
  | { kind: 'yawTo'; yawDeg: number; degPerTick: number; until: Until }
  | { kind: 'carve'; yawDeg: number; degPerTick: number; right: 1 | -1; until: Until }
  | {
      kind: 'featherLane';
      originX: number;
      originZ: number;
      headingDeg: number;
      offset: number;
      side: 1 | -1;
      yawFollow: boolean;
      turnPerTick?: number;
      targetHeadingDeg?: number;
      until: Until;
    };

export interface BotRoute {
  phases: RoutePhase[];
  maxTicks: number;
}

export interface BotResult {
  finished: boolean;
  valid: boolean;
  ticks: number;
  splits: number[];
  topSpeed: number;
  died: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isUntilMet(
  until: Until,
  phaseTick: number,
  player: PlayerController,
  timer: RunTimer,
): boolean {
  if (until.tick !== undefined && phaseTick < until.tick) return false;
  if (until.checkpoint !== undefined && timer.splits.length < until.checkpoint) return false;
  if (until.zPast !== undefined && player.position.z < until.zPast) return false;
  if (until.zBefore !== undefined && player.position.z > until.zBefore) return false;
  if (until.xPast !== undefined && player.position.x < until.xPast) return false;
  if (until.xBefore !== undefined && player.position.x > until.xBefore) return false;
  if (until.nearXZ !== undefined) {
    const dx = player.position.x - until.nearXZ.x;
    const dz = player.position.z - until.nearXZ.z;
    if (Math.hypot(dx, dz) >= until.nearXZ.radius) return false;
  }
  return true;
}

function computeInput(
  phase: RoutePhase,
  player: PlayerController,
  effectiveLaneHeading?: number,
): { forward: number; right: number; jump: boolean } {
  switch (phase.kind) {
    case 'featherX': {
      const active =
        phase.side === -1
          ? player.position.x > phase.threshold
          : player.position.x < phase.threshold;
      return { forward: 0, right: active ? phase.side : 0, jump: false };
    }
    case 'hold':
      return { forward: phase.forward, right: phase.right, jump: false };
    case 'yawTo':
      return { forward: 0, right: 0, jump: false };
    case 'carve':
      return { forward: 0, right: phase.right, jump: false };
    case 'featherLane': {
      // rightVec at current heading: (cos h, 0, -sin h)
      // Derived from laneBuilder: advance dir = (sin h, 0, cos h),
      // so right = rotate 90° CW = (cos h, 0, -sin h).
      const headingDeg = effectiveLaneHeading ?? phase.headingDeg;
      const h = (headingDeg * Math.PI) / 180;
      const rightX = Math.cos(h);
      const rightZ = -Math.sin(h);
      const dx = player.position.x - phase.originX;
      const dz = player.position.z - phase.originZ;
      const d = dx * rightX + dz * rightZ; // signed lateral distance from lane centre
      // Press toward face (side direction) when not yet at target offset
      const active = phase.side * d < phase.side * phase.offset;
      return { forward: 0, right: active ? phase.side : 0, jump: false };
    }
  }
}

// ---------------------------------------------------------------------------
// Chunked runner (internal stepper)
// ---------------------------------------------------------------------------

export interface RouteRunner {
  /** Run up to n ticks.  Returns true when complete (finished or maxTicks hit). */
  stepMany(n: number): boolean;
  result(): BotResult;
  currentYaw(): number;
}

/**
 * Create a runner that advances the simulation in discrete steps.
 * `runRoute` is built on top of this.
 */
export function createRouteRunner(
  player: PlayerController,
  timer: RunTimer,
  route: BotRoute,
  startYaw = 0,
  onTick?: (tick: number) => void,
): RouteRunner {
  let phaseIndex = 0;
  let phaseTick = 0;
  let totalTick = 0;
  let topSpeed = 0;
  let yaw = startYaw;
  // Per-phase mutable heading for featherLane yawFollow (keyed by phase index).
  const laneHeadings: Map<number, number> = new Map();

  // Death detection: watch the controller's respawn counter rather than
  // wrapping `respawn`. Patrol dummies re-create a runner on every route loop,
  // and a wrapper would nest one closure per loop, forever.
  const respawnsAtStart = player.respawnCount;
  const hasDied = (): boolean => player.respawnCount > respawnsAtStart;

  function currentPhase(): RoutePhase | undefined {
    return route.phases[phaseIndex];
  }

  function isDone(): boolean {
    return timer.state === 'finished' || totalTick >= route.maxTicks;
  }

  function stepOne(): void {
    const phase = currentPhase();
    if (!phase) return;

    // Advance yaw for yawTo and carve phases.
    if (phase.kind === 'yawTo' || phase.kind === 'carve') {
      const targetRad = (phase.yawDeg * Math.PI) / 180;
      const diff = targetRad - yaw;
      // Wrap diff to [-PI, PI].
      const wrappedDiff = ((diff + Math.PI) % (2 * Math.PI)) - Math.PI;
      const stepRad = (phase.degPerTick * Math.PI) / 180;
      if (Math.abs(wrappedDiff) <= stepRad) {
        yaw = targetRad;
      } else {
        yaw += Math.sign(wrappedDiff) * stepRad;
      }
    }

    // Advance heading for featherLane phases.
    // Use per-runner laneHeadings map so the phase object is never mutated.
    // Yaw convention: yaw = headingRad (not +PI) so that:
    //   right=+1 → wish direction = lane's right vec (cos h, 0, −sin h)
    //   right=−1 → wish direction = lane's left vec  (−cos h, 0, sin h)
    // This aligns strafe with the ramp face regardless of camera facing direction.
    let effectiveLaneHeading: number | undefined;
    if (phase.kind === 'featherLane') {
      if (!laneHeadings.has(phaseIndex)) {
        laneHeadings.set(phaseIndex, phase.headingDeg);
        // Initialize yaw to lane heading so strafes align with ramp face immediately.
        yaw = (phase.headingDeg * Math.PI) / 180;
      }
      let currentLaneHeading = laneHeadings.get(phaseIndex) as number;
      if (phase.yawFollow && phase.turnPerTick !== undefined) {
        const target = phase.targetHeadingDeg ?? phase.headingDeg;
        const diff = target - currentLaneHeading;
        const step = Math.min(Math.abs(diff), phase.turnPerTick) * Math.sign(diff || 0);
        currentLaneHeading += step;
        laneHeadings.set(phaseIndex, currentLaneHeading);
        // Yaw tracks lane heading so strafe stays aligned through the bend.
        yaw = (currentLaneHeading * Math.PI) / 180;
      }
      effectiveLaneHeading = currentLaneHeading;
    }

    const input = computeInput(phase, player, effectiveLaneHeading);
    player.tick(input, yaw, TICK_DT);
    timer.tick(player.position);
    topSpeed = Math.max(topSpeed, player.horizontalSpeed());
    phaseTick++;
    totalTick++;

    onTick?.(totalTick);

    // Advance phase if until condition is met.
    if (isUntilMet(phase.until, phaseTick, player, timer)) {
      phaseIndex++;
      phaseTick = 0;
      // If no more phases, the bot is done driving; let maxTicks finish.
    }
  }

  return {
    stepMany(n: number): boolean {
      for (let i = 0; i < n; i++) {
        if (isDone()) return true;
        stepOne();
        if (isDone()) return true;
      }
      return isDone();
    },

    result(): BotResult {
      return {
        finished: timer.state === 'finished',
        valid: timer.valid,
        ticks: timer.ticks,
        splits: timer.splits.map((s) => s.ticks),
        topSpeed,
        died: hasDied(),
      };
    },

    currentYaw(): number {
      return yaw;
    },
  };
}

// ---------------------------------------------------------------------------
// Synchronous entry point
// ---------------------------------------------------------------------------

/**
 * Run a full route synchronously (suitable for vitest / Node).
 * `onTick` is called after every simulation tick with the total tick count.
 */
export function runRoute(
  player: PlayerController,
  timer: RunTimer,
  route: BotRoute,
  startYaw = 0,
  onTick?: (tick: number) => void,
): BotResult {
  const runner = createRouteRunner(player, timer, route, startYaw, onTick);
  runner.stepMany(route.maxTicks);
  return runner.result();
}
